/**
 * Vitals API
 *
 * API functions for saving and fetching patient vitals as FHIR Observation resources.
 * Vitals are linked to Encounters for proper visit context.
 */

import { apiClient } from './client';
import { createComponentLogger } from '@/lib/utils/logger';
import type { VitalSigns } from '@/lib/types/consultation';

const logger = createComponentLogger('vitalsApi');

/**
 * LOINC codes for vital signs
 * @see https://loinc.org/
 */
export const VITAL_LOINC_CODES = {
  // Blood Pressure Panel (contains systolic and diastolic)
  bloodPressurePanel: {
    code: '85354-9',
    display: 'Blood pressure panel with all children optional',
    system: 'http://loinc.org',
  },
  systolicBP: {
    code: '8480-6',
    display: 'Systolic blood pressure',
    system: 'http://loinc.org',
  },
  diastolicBP: {
    code: '8462-4',
    display: 'Diastolic blood pressure',
    system: 'http://loinc.org',
  },
  heartRate: {
    code: '8867-4',
    display: 'Heart rate',
    system: 'http://loinc.org',
  },
  temperature: {
    code: '8310-5',
    display: 'Body temperature',
    system: 'http://loinc.org',
  },
  respiratoryRate: {
    code: '9279-1',
    display: 'Respiratory rate',
    system: 'http://loinc.org',
  },
  oxygenSaturation: {
    code: '59408-5',
    display: 'Oxygen saturation in Arterial blood by Pulse oximetry',
    system: 'http://loinc.org',
  },
  bloodSugar: {
    code: '2339-0',
    display: 'Glucose [Mass/volume] in Blood',
    system: 'http://loinc.org',
  },
  hemoglobin: {
    code: '718-7',
    display: 'Hemoglobin [Mass/volume] in Blood',
    system: 'http://loinc.org',
  },
  cholesterol: {
    code: '2093-3',
    display: 'Cholesterol [Mass/volume] in Serum or Plasma',
    system: 'http://loinc.org',
  },
  weight: {
    code: '29463-7',
    display: 'Body weight',
    system: 'http://loinc.org',
  },
  height: {
    code: '8302-2',
    display: 'Body height',
    system: 'http://loinc.org',
  },
} as const;

/**
 * FHIR Observation resource structure
 */
interface ObservationResource {
  resourceType: 'Observation';
  id?: string;
  status:
    | 'registered'
    | 'preliminary'
    | 'final'
    | 'amended'
    | 'corrected'
    | 'cancelled'
    | 'entered-in-error'
    | 'unknown';
  category?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
  }>;
  code: {
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  };
  subject?: {
    reference?: string;
    display?: string;
  };
  encounter?: {
    reference?: string;
  };
  effectiveDateTime?: string;
  valueQuantity?: {
    value?: number;
    unit?: string;
    system?: string;
    code?: string;
  };
  valueString?: string;
  component?: Array<{
    code: {
      coding?: Array<{
        system?: string;
        code?: string;
        display?: string;
      }>;
    };
    valueQuantity?: {
      value?: number;
      unit?: string;
      system?: string;
      code?: string;
    };
  }>;
  performer?: Array<{
    reference?: string;
    display?: string;
  }>;
}

/**
 * FHIR Bundle for search results
 */
interface ObservationBundle {
  resourceType: 'Bundle';
  type: string;
  total?: number;
  entry?: Array<{
    resource?: ObservationResource;
  }>;
}

/**
 * Request to save vitals
 */
export interface SaveVitalsRequest {
  patientId: string;
  encounterId: string;
  vitals: VitalSigns;
  practitionerId?: string;
}

/**
 * Request to update vitals for an encounter that may already have some recorded
 */
export interface UpdateVitalsRequest {
  patientId: string;
  encounterId: string;
  vitals: VitalSigns;
  practitionerId?: string;
}

/**
 * Helper to create a vital signs category
 */
function createVitalSignsCategory() {
  return [
    {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/observation-category',
          code: 'vital-signs',
          display: 'Vital Signs',
        },
      ],
    },
  ];
}

/**
 * Helper to format reference
 */
function toReference(resourceType: string, id: string): string {
  if (id.includes('/')) {
    return id;
  }
  return `${resourceType}/${id}`;
}

/**
 * Create an Observation resource for a single vital
 */
function createObservationResource(params: {
  patientId: string;
  encounterId: string;
  loincCode: (typeof VITAL_LOINC_CODES)[keyof typeof VITAL_LOINC_CODES];
  value: number;
  unit: string;
  unitCode: string;
  practitionerId?: string;
}): Partial<ObservationResource> {
  const resource: Partial<ObservationResource> = {
    resourceType: 'Observation',
    status: 'final',
    category: createVitalSignsCategory(),
    code: {
      coding: [
        {
          system: params.loincCode.system,
          code: params.loincCode.code,
          display: params.loincCode.display,
        },
      ],
      text: params.loincCode.display,
    },
    subject: {
      reference: toReference('Patient', params.patientId),
    },
    encounter: {
      reference: toReference('Encounter', params.encounterId),
    },
    effectiveDateTime: new Date().toISOString(),
    valueQuantity: {
      value: params.value,
      unit: params.unit,
      system: 'http://unitsofmeasure.org',
      code: params.unitCode,
    },
  };

  if (params.practitionerId) {
    resource.performer = [
      {
        reference: toReference('Practitioner', params.practitionerId),
      },
    ];
  }

  return resource;
}

/**
 * Create Blood Pressure Observation with components
 */
function createBloodPressureObservation(params: {
  patientId: string;
  encounterId: string;
  systolic: number;
  diastolic: number;
  practitionerId?: string;
}): Partial<ObservationResource> {
  const resource: Partial<ObservationResource> = {
    resourceType: 'Observation',
    status: 'final',
    category: createVitalSignsCategory(),
    code: {
      coding: [
        {
          system: VITAL_LOINC_CODES.bloodPressurePanel.system,
          code: VITAL_LOINC_CODES.bloodPressurePanel.code,
          display: VITAL_LOINC_CODES.bloodPressurePanel.display,
        },
      ],
      text: 'Blood pressure',
    },
    subject: {
      reference: toReference('Patient', params.patientId),
    },
    encounter: {
      reference: toReference('Encounter', params.encounterId),
    },
    effectiveDateTime: new Date().toISOString(),
    component: [
      {
        code: {
          coding: [
            {
              system: VITAL_LOINC_CODES.systolicBP.system,
              code: VITAL_LOINC_CODES.systolicBP.code,
              display: VITAL_LOINC_CODES.systolicBP.display,
            },
          ],
        },
        valueQuantity: {
          value: params.systolic,
          unit: 'mmHg',
          system: 'http://unitsofmeasure.org',
          code: 'mm[Hg]',
        },
      },
      {
        code: {
          coding: [
            {
              system: VITAL_LOINC_CODES.diastolicBP.system,
              code: VITAL_LOINC_CODES.diastolicBP.code,
              display: VITAL_LOINC_CODES.diastolicBP.display,
            },
          ],
        },
        valueQuantity: {
          value: params.diastolic,
          unit: 'mmHg',
          system: 'http://unitsofmeasure.org',
          code: 'mm[Hg]',
        },
      },
    ],
  };

  if (params.practitionerId) {
    resource.performer = [
      {
        reference: toReference('Practitioner', params.practitionerId),
      },
    ];
  }

  return resource;
}

/**
 * Fetch the raw vital-signs Observation resources (with ids) linked to an Encounter —
 * used by updateVitals to decide whether to PUT onto an existing Observation or POST
 * a new one, since getVitalsForEncounter discards ids after parsing into VitalSigns.
 */
async function fetchEncounterVitalObservations(
  encounterId: string
): Promise<ObservationResource[]> {
  const encounterRef = encounterId.includes('/') ? encounterId : `Encounter/${encounterId}`;
  const searchParams = new URLSearchParams();
  searchParams.set('encounter', encounterRef);
  searchParams.set('category', 'vital-signs');
  searchParams.set('_count', '100');

  const response = await apiClient.get<ObservationBundle>(
    `/Observation?${searchParams.toString()}`
  );
  return (
    response.data.entry
      ?.map((e) => e.resource)
      .filter((resource): resource is ObservationResource => !!resource) || []
  );
}

/**
 * Parse blood pressure string (e.g., "120/80") into systolic and diastolic
 * Includes medical validation for reasonable physiological ranges
 */
function parseBloodPressure(bp: string): { systolic: number; diastolic: number } | null {
  const match = bp.trim().match(/^(\d+)\/(\d+)$/);
  if (!match) {
    return null;
  }
  const systolic = parseInt(match[1], 10);
  const diastolic = parseInt(match[2], 10);

  // Medical validation: systolic should be greater than diastolic
  // and both should be within reasonable physiological ranges
  const isSystolicInRange = systolic >= 60 && systolic <= 250;
  const isDiastolicInRange = diastolic >= 40 && diastolic <= 150;
  const isRelationshipValid = systolic > diastolic;

  if (!isSystolicInRange || !isDiastolicInRange || !isRelationshipValid) {
    logger.warn('Blood pressure validation failed:', { systolic, diastolic });
    return null;
  }

  return { systolic, diastolic };
}

/**
 * Parse an array of FHIR Observation resources into a VitalSigns object.
 * Handles the blood pressure panel format and legacy standalone BP codes.
 */
function parseObservationsToVitalSigns(
  observations: (ObservationResource | undefined)[]
): VitalSigns {
  const vitals: VitalSigns = {};
  let legacySystolic: number | undefined;
  let legacyDiastolic: number | undefined;

  for (const obs of observations) {
    if (!obs) continue;

    const code = obs.code?.coding?.[0]?.code;

    switch (code) {
      case VITAL_LOINC_CODES.bloodPressurePanel.code: {
        // Blood pressure panel with components (preferred format)
        const systolic = obs.component?.find(
          (c) => c.code?.coding?.[0]?.code === VITAL_LOINC_CODES.systolicBP.code
        )?.valueQuantity?.value;
        const diastolic = obs.component?.find(
          (c) => c.code?.coding?.[0]?.code === VITAL_LOINC_CODES.diastolicBP.code
        )?.valueQuantity?.value;
        if (systolic && diastolic) {
          vitals.bloodPressure = `${systolic}/${diastolic}`;
        }
        break;
      }
      // Backward-compat: old kiosk saved systolic/diastolic as separate Observations
      case VITAL_LOINC_CODES.systolicBP.code:
        legacySystolic = obs.valueQuantity?.value;
        break;
      case VITAL_LOINC_CODES.diastolicBP.code:
        legacyDiastolic = obs.valueQuantity?.value;
        break;
      case VITAL_LOINC_CODES.heartRate.code:
        vitals.heartRate = obs.valueQuantity?.value;
        break;
      case VITAL_LOINC_CODES.temperature.code:
        vitals.temperature = obs.valueQuantity?.value;
        break;
      case VITAL_LOINC_CODES.respiratoryRate.code:
        vitals.respiratoryRate = obs.valueQuantity?.value;
        break;
      case VITAL_LOINC_CODES.oxygenSaturation.code:
        vitals.oxygenSaturation = obs.valueQuantity?.value;
        break;
      // Backward-compat: old kiosk used wrong SpO2 code 2708-6
      case '2708-6':
        if (!vitals.oxygenSaturation) {
          vitals.oxygenSaturation = obs.valueQuantity?.value;
        }
        break;
      case VITAL_LOINC_CODES.bloodSugar.code:
        vitals.bloodSugar = obs.valueQuantity?.value;
        break;
      case VITAL_LOINC_CODES.weight.code:
        vitals.weight = obs.valueQuantity?.value;
        break;
      case VITAL_LOINC_CODES.height.code:
        vitals.height = obs.valueQuantity?.value;
        break;
    }
  }

  // Merge legacy standalone BP components if panel observation was not found
  if (!vitals.bloodPressure && legacySystolic != null && legacyDiastolic != null) {
    vitals.bloodPressure = `${legacySystolic}/${legacyDiastolic}`;
  }

  return vitals;
}

/**
 * Fetch kiosk vitals for a patient via the BFF (patient-tagged observations, no encounter link).
 */
async function getKioskVitalsForPatient(patientId: string): Promise<VitalSigns> {
  const PRANIK_HMIS_V2 = `${import.meta.env.VITE_AUTH_API_URL}/v2`;
  const response = await apiClient.get<{ observations: ObservationResource[] }>(
    `/kiosk/vitals/${patientId}`,
    { baseUrl: PRANIK_HMIS_V2 }
  );
  const observations = response.data?.observations ?? [];
  logger.log(`Fetched ${observations.length} kiosk vitals for patient ${patientId}`);
  return parseObservationsToVitalSigns(observations);
}

/**
 * Vitals API
 */
export const vitalsApi = {
  /**
   * Save vitals as Observation resources linked to an Encounter
   *
   * @param request - Save vitals request with patient, encounter, and vitals data
   * @returns Promise with array of created Observation IDs
   *
   * @example
   * ```tsx
   * const observationIds = await vitalsApi.saveVitals({
   *   patientId: 'patient-123',
   *   encounterId: 'encounter-456',
   *   vitals: {
   *     bloodPressure: '120/80',
   *     heartRate: 72,
   *     temperature: 98.6,
   *     oxygenSaturation: 98,
   *     weight: 70,
   *     height: 175,
   *   },
   * });
   * ```
   */
  saveVitals: async (request: SaveVitalsRequest): Promise<string[]> => {
    const { patientId, encounterId, vitals, practitionerId } = request;
    const createdIds: string[] = [];

    logger.log('Saving vitals for encounter:', encounterId);

    try {
      // Blood Pressure (as panel with components)
      if (vitals.bloodPressure) {
        const bp = parseBloodPressure(vitals.bloodPressure);
        if (bp) {
          const observation = createBloodPressureObservation({
            patientId,
            encounterId,
            systolic: bp.systolic,
            diastolic: bp.diastolic,
            practitionerId,
          });
          const response = await apiClient.post<ObservationResource>('/Observation', {
            body: observation,
          });
          if (response.data.id) {
            createdIds.push(response.data.id);
            logger.log('Created blood pressure observation:', response.data.id);
          }
        } else {
          logger.warn('Invalid blood pressure format, skipping Observation creation', {
            input: vitals.bloodPressure,
            patientId,
            encounterId,
          });
        }
      }

      // Heart Rate
      if (vitals.heartRate && !isNaN(vitals.heartRate)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.heartRate,
          value: vitals.heartRate,
          unit: 'beats/minute',
          unitCode: '/min',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created heart rate observation:', response.data.id);
        }
      }

      // Temperature
      if (vitals.temperature && !isNaN(vitals.temperature)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.temperature,
          value: vitals.temperature,
          unit: '°F',
          unitCode: '[degF]',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created temperature observation:', response.data.id);
        }
      }

      // Respiratory Rate
      if (vitals.respiratoryRate && !isNaN(vitals.respiratoryRate)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.respiratoryRate,
          value: vitals.respiratoryRate,
          unit: 'breaths/minute',
          unitCode: '/min',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created respiratory rate observation:', response.data.id);
        }
      }

      // Oxygen Saturation (SpO2)
      if (vitals.oxygenSaturation && !isNaN(vitals.oxygenSaturation)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.oxygenSaturation,
          value: vitals.oxygenSaturation,
          unit: '%',
          unitCode: '%',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created oxygen saturation observation:', response.data.id);
        }
      }

      // Blood Sugar
      if (vitals.bloodSugar && !isNaN(vitals.bloodSugar)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.bloodSugar,
          value: vitals.bloodSugar,
          unit: 'mg/dL',
          unitCode: 'mg/dL',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created blood sugar observation:', response.data.id);
        }
      }

      // Weight
      if (vitals.weight && !isNaN(vitals.weight)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.weight,
          value: vitals.weight,
          unit: 'kg',
          unitCode: 'kg',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created weight observation:', response.data.id);
        }
      }

      // Height
      if (vitals.height && !isNaN(vitals.height)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.height,
          value: vitals.height,
          unit: 'cm',
          unitCode: 'cm',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created height observation:', response.data.id);
        }
      }

      // Hemoglobin
      if (vitals.hemoglobin && !isNaN(vitals.hemoglobin)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.hemoglobin,
          value: vitals.hemoglobin,
          unit: 'g/dL',
          unitCode: 'g/dL',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created hemoglobin observation:', response.data.id);
        }
      }

      // Cholesterol
      if (vitals.cholesterol && !isNaN(vitals.cholesterol)) {
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: VITAL_LOINC_CODES.cholesterol,
          value: vitals.cholesterol,
          unit: 'mg/dL',
          unitCode: 'mg/dL',
          practitionerId,
        });
        const response = await apiClient.post<ObservationResource>('/Observation', {
          body: observation,
        });
        if (response.data.id) {
          createdIds.push(response.data.id);
          logger.log('Created cholesterol observation:', response.data.id);
        }
      }

      logger.log(`Successfully saved ${createdIds.length} vital observations`);
      return createdIds;
    } catch (error) {
      logger.error('Failed to save vitals:', error);
      throw error;
    }
  },

  /**
   * Update vitals for an encounter that may already have some recorded — edits an
   * existing vital's Observation in place (PUT, preserving its id/history) when one
   * is found for that LOINC code, and creates a new Observation (POST) otherwise.
   * Vitals the caller leaves blank are left untouched rather than deleted.
   *
   * @example
   * ```tsx
   * await vitalsApi.updateVitals({
   *   patientId: 'patient-123',
   *   encounterId: 'encounter-456',
   *   vitals: { weight: 71, bloodPressure: '118/78' },
   * });
   * ```
   */
  updateVitals: async (request: UpdateVitalsRequest): Promise<void> => {
    const { patientId, encounterId, vitals, practitionerId } = request;

    try {
      const existingObservations = await fetchEncounterVitalObservations(encounterId);
      const findExisting = (code: string) =>
        existingObservations.find((obs) => obs.code?.coding?.[0]?.code === code);

      const upsert = async (observation: Partial<ObservationResource>, existingId?: string) => {
        if (existingId) {
          await apiClient.put(`/Observation/${existingId}`, {
            body: { ...observation, id: existingId, resourceType: 'Observation' },
          });
        } else {
          await apiClient.post('/Observation', { body: observation });
        }
      };

      if (vitals.bloodPressure) {
        const bp = parseBloodPressure(vitals.bloodPressure);
        if (bp) {
          const observation = createBloodPressureObservation({
            patientId,
            encounterId,
            systolic: bp.systolic,
            diastolic: bp.diastolic,
            practitionerId,
          });
          await upsert(observation, findExisting(VITAL_LOINC_CODES.bloodPressurePanel.code)?.id);
        } else {
          logger.warn('Invalid blood pressure format, skipping update', {
            input: vitals.bloodPressure,
          });
        }
      }

      const simpleVitals: Array<{
        value: number | undefined;
        loincCode: (typeof VITAL_LOINC_CODES)[keyof typeof VITAL_LOINC_CODES];
        unit: string;
        unitCode: string;
      }> = [
        {
          value: vitals.heartRate,
          loincCode: VITAL_LOINC_CODES.heartRate,
          unit: 'beats/minute',
          unitCode: '/min',
        },
        {
          value: vitals.temperature,
          loincCode: VITAL_LOINC_CODES.temperature,
          unit: '°F',
          unitCode: '[degF]',
        },
        {
          value: vitals.respiratoryRate,
          loincCode: VITAL_LOINC_CODES.respiratoryRate,
          unit: 'breaths/minute',
          unitCode: '/min',
        },
        {
          value: vitals.oxygenSaturation,
          loincCode: VITAL_LOINC_CODES.oxygenSaturation,
          unit: '%',
          unitCode: '%',
        },
        {
          value: vitals.bloodSugar,
          loincCode: VITAL_LOINC_CODES.bloodSugar,
          unit: 'mg/dL',
          unitCode: 'mg/dL',
        },
        { value: vitals.weight, loincCode: VITAL_LOINC_CODES.weight, unit: 'kg', unitCode: 'kg' },
        { value: vitals.height, loincCode: VITAL_LOINC_CODES.height, unit: 'cm', unitCode: 'cm' },
        {
          value: vitals.hemoglobin,
          loincCode: VITAL_LOINC_CODES.hemoglobin,
          unit: 'g/dL',
          unitCode: 'g/dL',
        },
        {
          value: vitals.cholesterol,
          loincCode: VITAL_LOINC_CODES.cholesterol,
          unit: 'mg/dL',
          unitCode: 'mg/dL',
        },
      ];

      for (const v of simpleVitals) {
        if (v.value === undefined || v.value === null || isNaN(v.value)) continue;
        const observation = createObservationResource({
          patientId,
          encounterId,
          loincCode: v.loincCode,
          value: v.value,
          unit: v.unit,
          unitCode: v.unitCode,
          practitionerId,
        });
        await upsert(observation, findExisting(v.loincCode.code)?.id);
      }

      logger.log('Vitals updated successfully for encounter:', encounterId);
    } catch (error) {
      logger.error('Failed to update vitals:', error);
      throw error;
    }
  },

  /**
   * Fetch vitals for an Encounter
   *
   * @param encounterId - Encounter ID to fetch vitals for
   * @returns Promise with VitalSigns object
   *
   * @example
   * ```tsx
   * const vitals = await vitalsApi.getVitalsForEncounter('encounter-456');
   * logger.log(vitals.bloodPressure); // "120/80"
   * logger.log(vitals.heartRate); // 72
   * ```
   */
  getVitalsForEncounter: async (encounterId: string, patientId?: string): Promise<VitalSigns> => {
    try {
      const encounterRef = encounterId.includes('/') ? encounterId : `Encounter/${encounterId}`;

      logger.log('Fetching vitals for encounter:', encounterRef);

      // Search for vital signs Observations linked to this encounter
      const searchParams = new URLSearchParams();
      searchParams.set('encounter', encounterRef);
      searchParams.set('category', 'vital-signs');
      searchParams.set('_count', '100'); // Get all vitals for the encounter

      const response = await apiClient.get<ObservationBundle>(
        `/Observation?${searchParams.toString()}`
      );

      const bundle = response.data;
      const observations = bundle.entry?.map((e) => e.resource).filter(Boolean) || [];

      logger.log(`Found ${observations.length} vital observations`);

      const vitals = parseObservationsToVitalSigns(observations);

      // If no vitals found via encounter and we have a patientId, fall back to kiosk vitals
      const hasAnyVital = Object.values(vitals).some((v) => v != null);
      if (!hasAnyVital && patientId) {
        logger.log(
          'No encounter vitals found, falling back to kiosk vitals for patient:',
          patientId
        );
        try {
          return await getKioskVitalsForPatient(patientId);
        } catch (fallbackError) {
          logger.warn('Kiosk vitals fallback failed:', fallbackError);
        }
      }

      logger.log('Parsed vitals:', vitals);
      return vitals;
    } catch (error) {
      logger.error('Failed to fetch vitals:', error);
      // Vitals are non-critical; fail gracefully and return an empty result
      return {} as VitalSigns;
    }
  },

  /**
   * Check if an encounter has any vitals recorded
   *
   * @param encounterId - Encounter ID to check
   * @returns Promise with boolean indicating if vitals exist
   */
  hasVitals: async (encounterId: string): Promise<boolean> => {
    try {
      const encounterRef = encounterId.includes('/') ? encounterId : `Encounter/${encounterId}`;

      const searchParams = new URLSearchParams();
      searchParams.set('encounter', encounterRef);
      searchParams.set('category', 'vital-signs');
      searchParams.set('_count', '1');
      searchParams.set('_summary', 'count');

      const response = await apiClient.get<ObservationBundle>(
        `/Observation?${searchParams.toString()}`
      );

      return (response.data.total ?? 0) > 0;
    } catch (error) {
      logger.error('Failed to check vitals existence:', error);
      return false;
    }
  },
};

export default vitalsApi;
